import { useState, type FormEvent } from "react";
import type { RegisterClientResult } from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import selectChevron from "../../assets/select-chevron.svg";
import { writeClientSession } from "../cabinet/client-session";
import { SmartCaptchaField } from "../captcha/SmartCaptchaField";
import { REGISTRATION_REGIONS } from "../registration/regions";

type RegistrationFormProps = {
  api: PublicCatalogApi;
  /** Если null — standalone POST /clients/register (отдельная страница). */
  projectId: string | null;
  /** page — макет Figma 1:1208; wizard — компактная форма после подбора. */
  layout?: "page" | "wizard";
  onRegistered?: (result: RegisterClientResult) => void;
  onGoWizard?: () => void;
  onGoCabinet?: () => void;
  onSessionSaved?: () => void;
};

type Draft = {
  company: string;
  position: string;
  fullName: string;
  lastName: string;
  firstName: string;
  patronymic: string;
  phone: string;
  email: string;
  password: string;
  passwordConfirm: string;
  region: string;
  deliveryObject: string;
  consentAccepted: boolean;
};

const EMPTY_DRAFT: Draft = {
  company: "",
  position: "",
  fullName: "",
  lastName: "",
  firstName: "",
  patronymic: "",
  phone: "",
  email: "",
  password: "",
  passwordConfirm: "",
  region: "",
  deliveryObject: "",
  consentAccepted: false,
};

export function RegistrationForm({
  api,
  projectId,
  layout = "wizard",
  onRegistered,
  onGoWizard,
  onGoCabinet,
  onSessionSaved,
}: RegistrationFormProps) {
  const [draft, setDraft] = useState<Draft>(() => ({ ...EMPTY_DRAFT }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [registered, setRegistered] = useState<RegisterClientResult | null>(null);
  const isPage = layout === "page";

  function updateDraft<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...EMPTY_DRAFT, ...current, [key]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const company = String(draft.company ?? "").trim();
    const positionRaw = String(draft.position ?? "").trim();
    const fullName = isPage
      ? [draft.lastName, draft.firstName, draft.patronymic]
          .map((part) => String(part ?? "").trim())
          .filter(Boolean)
          .join(" ")
      : String(draft.fullName ?? "").trim();
    const phone = String(draft.phone ?? "").trim();
    const email = String(draft.email ?? "").trim();
    const password = String(draft.password ?? "");
    const passwordConfirm = String(draft.passwordConfirm ?? "");
    const region = String(draft.region ?? "").trim();
    const deliveryObject = String(draft.deliveryObject ?? "").trim();

    if (password.length < 12) {
      setError("Пароль должен быть не короче 12 символов");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Пароль и подтверждение не совпадают");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const payload = {
        company,
        position: positionRaw || (isPage ? "не указана" : ""),
        fullName,
        phone,
        email,
        password,
        region: normalizeOptional(region),
        deliveryObject: isPage ? null : normalizeOptional(deliveryObject),
        consentAccepted: true as const,
        captchaToken: captchaToken || undefined,
      };

      const result =
        projectId === null
          ? await api.registerClientStandalone(payload)
          : await api.registerClient(projectId, payload);
      writeClientSession({
        personalCode: result.personalCode,
        clientId: result.clientId,
        fullName,
        company,
        position: payload.position,
        region: payload.region ?? null,
      });
      onSessionSaved?.();
      setRegistered(result);
      onRegistered?.(result);
    } catch (caught: unknown) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  if (registered) {
    return (
      <div className={isPage ? "registration-form registration-form--page" : "registration-form"}>
        <p className="wizard-step__status" role="status">
          Регистрация завершена. Ваш персональный код: <strong>{registered.personalCode}</strong>
        </p>
        {registered.linkedProjectCount > 0 ? (
          <p className="wizard-step__hint">
            Сохранено проектов: {registered.linkedProjectCount}. Этот код используйте для следующих
            входов в систему.
          </p>
        ) : (
          <p className="wizard-step__hint">
            Сохраните код — он понадобится для входа в кабинет и продолжения подборов.
          </p>
        )}
        <div className="registration-form__actions">
          {onGoCabinet ? (
            <button type="button" className="registration-form__submit" onClick={onGoCabinet}>
              В кабинет
            </button>
          ) : null}
          {onGoWizard ? (
            <button type="button" className="registration-form__back" onClick={onGoWizard}>
              К подбору
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (isPage) {
    return (
      <form
        className="registration-form registration-form--page"
        onSubmit={(event) => void submit(event)}
      >
        {error ? (
          <p className="wizard-step__status wizard-step__status--error" role="alert">
            {error}
          </p>
        ) : null}

        <section className="registration-form__section" aria-labelledby="reg-org-title">
          <h2 id="reg-org-title" className="registration-form__section-title">
            Организация
          </h2>
          <div className="registration-form__stack">
            <label className="registration-form__field">
              <span className="registration-form__label">
                Компания / организация
                <span className="registration-form__req" aria-hidden>
                  *
                </span>
              </span>
              <input
                value={draft.company ?? ""}
                onChange={(event) =>
                  updateDraft("company", event.target.value)
                }
                placeholder='ООО «Промтехника»'
                required
                disabled={busy}
              />
            </label>
            <label className="registration-form__field">
              <span className="registration-form__label">Должность</span>
              <input
                value={draft.position ?? ""}
                onChange={(event) =>
                  updateDraft("position", event.target.value)
                }
                placeholder="Главный механик"
                disabled={busy}
              />
            </label>
          </div>
        </section>

        <section className="registration-form__section" aria-labelledby="reg-person-title">
          <h2 id="reg-person-title" className="registration-form__section-title">
            Контактное лицо
          </h2>
          <div className="registration-form__row registration-form__row--3">
            <label className="registration-form__field">
              <span className="registration-form__label">
                Фамилия
                <span className="registration-form__req" aria-hidden>
                  *
                </span>
              </span>
              <input
                value={draft.lastName ?? ""}
                onChange={(event) =>
                  updateDraft("lastName", event.target.value)
                }
                placeholder="Иванов"
                required
                disabled={busy}
              />
            </label>
            <label className="registration-form__field">
              <span className="registration-form__label">
                Имя
                <span className="registration-form__req" aria-hidden>
                  *
                </span>
              </span>
              <input
                value={draft.firstName ?? ""}
                onChange={(event) =>
                  updateDraft("firstName", event.target.value)
                }
                placeholder="Иван"
                required
                disabled={busy}
              />
            </label>
            <label className="registration-form__field">
              <span className="registration-form__label">Отчество</span>
              <input
                value={draft.patronymic ?? ""}
                onChange={(event) =>
                  updateDraft("patronymic", event.target.value)
                }
                placeholder="Иванович"
                disabled={busy}
              />
            </label>
          </div>
        </section>

        <section className="registration-form__section" aria-labelledby="reg-contacts-title">
          <h2 id="reg-contacts-title" className="registration-form__section-title">
            Контакты
          </h2>
          <div className="registration-form__stack">
            <div className="registration-form__row registration-form__row--2">
              <label className="registration-form__field">
                <span className="registration-form__label">
                  Телефон
                  <span className="registration-form__req" aria-hidden>
                    *
                  </span>
                </span>
                <input
                  value={draft.phone ?? ""}
                  onChange={(event) =>
                    updateDraft("phone", event.target.value)
                  }
                  placeholder="+7 (XXX) XXX-XX-XX"
                  required
                  disabled={busy}
                />
              </label>
              <label className="registration-form__field">
                <span className="registration-form__label">
                  E-mail
                  <span className="registration-form__req" aria-hidden>
                    *
                  </span>
                </span>
                <input
                  type="email"
                  value={draft.email ?? ""}
                  onChange={(event) =>
                    updateDraft("email", event.target.value)
                  }
                  placeholder="ivan@example.ru"
                  required
                  disabled={busy}
                />
              </label>
            </div>
            <label className="registration-form__field">
              <span className="registration-form__label">
                Регион
                <span className="registration-form__req" aria-hidden>
                  *
                </span>
              </span>
              <span className="registration-form__select-wrap">
                <select
                  value={draft.region ?? ""}
                  onChange={(event) =>
                    updateDraft("region", event.target.value)
                  }
                  required
                  disabled={busy}
                >
                  <option value="">— выберите регион —</option>
                  {REGISTRATION_REGIONS.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
                <img
                  className="registration-form__select-chevron"
                  src={selectChevron}
                  alt=""
                  width={10}
                  height={6}
                  aria-hidden
                />
              </span>
            </label>
          </div>
        </section>

        <section className="registration-form__section" aria-labelledby="reg-account-title">
          <h2 id="reg-account-title" className="registration-form__section-title">
            Учётная запись
          </h2>
          <div className="registration-form__row registration-form__row--2">
            <label className="registration-form__field">
              <span className="registration-form__label">
                Пароль
                <span className="registration-form__req" aria-hidden>
                  *
                </span>
              </span>
              <input
                type="password"
                value={draft.password ?? ""}
                onChange={(event) => updateDraft("password", event.target.value)}
                minLength={12}
                required
                disabled={busy}
                autoComplete="new-password"
              />
            </label>
            <label className="registration-form__field">
              <span className="registration-form__label">
                Подтверждение
                <span className="registration-form__req" aria-hidden>
                  *
                </span>
              </span>
              <input
                type="password"
                value={draft.passwordConfirm ?? ""}
                onChange={(event) => updateDraft("passwordConfirm", event.target.value)}
                minLength={12}
                required
                disabled={busy}
                autoComplete="new-password"
              />
            </label>
          </div>
        </section>

        <SmartCaptchaField onToken={setCaptchaToken} disabled={busy} />

        <label className="registration-form__consent-box">
          <input
            type="checkbox"
            checked={Boolean(draft.consentAccepted)}
            onChange={(event) =>
              updateDraft("consentAccepted", event.target.checked)
            }
            required
            disabled={busy}
          />
          <span>
            Я даю согласие на обработку персональных данных в соответствии с Федеральным законом
            №152-ФЗ «О персональных данных» и{" "}
            <a
              className="registration-form__policy"
              href="https://konmash.ru"
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              Политикой конфиденциальности
            </a>{" "}
            АО «КМЗКО»
          </span>
        </label>

        <div className="registration-form__footer">
          {onGoWizard ? (
            <button
              type="button"
              className="registration-form__back"
              onClick={onGoWizard}
              disabled={busy}
            >
              ← Назад
            </button>
          ) : null}
          <button
            type="submit"
            className="registration-form__submit"
            disabled={busy || !draft.consentAccepted}
          >
            Зарегистрироваться
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="registration-form" onSubmit={(event) => void submit(event)}>
      <h3>Регистрационные данные</h3>
      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      <label className="registration-form__field">
        <span>Компания</span>
        <input
          value={draft.company ?? ""}
          onChange={(event) => updateDraft("company", event.target.value)}
          required
          disabled={busy}
        />
      </label>
      <label className="registration-form__field">
        <span>Должность</span>
        <input
          value={draft.position ?? ""}
          onChange={(event) => updateDraft("position", event.target.value)}
          required
          disabled={busy}
        />
      </label>
      <label className="registration-form__field">
        <span>ФИО</span>
        <input
          value={draft.fullName ?? ""}
          onChange={(event) => updateDraft("fullName", event.target.value)}
          required
          disabled={busy}
        />
      </label>
      <label className="registration-form__field">
        <span>Мобильный телефон</span>
        <input
          value={draft.phone ?? ""}
          onChange={(event) => updateDraft("phone", event.target.value)}
          required
          disabled={busy}
        />
      </label>
      <label className="registration-form__field">
        <span>Рабочий e-mail</span>
        <input
          type="email"
          value={draft.email ?? ""}
          onChange={(event) => updateDraft("email", event.target.value)}
          required
          disabled={busy}
        />
      </label>
      <label className="registration-form__field">
        <span>Пароль (≥12)</span>
        <input
          type="password"
          value={draft.password ?? ""}
          onChange={(event) => updateDraft("password", event.target.value)}
          minLength={12}
          required
          disabled={busy}
          autoComplete="new-password"
        />
      </label>
      <label className="registration-form__field">
        <span>Подтверждение пароля</span>
        <input
          type="password"
          value={draft.passwordConfirm ?? ""}
          onChange={(event) => updateDraft("passwordConfirm", event.target.value)}
          minLength={12}
          required
          disabled={busy}
          autoComplete="new-password"
        />
      </label>
      <label className="registration-form__field">
        <span>Регион поставки</span>
        <input
          value={draft.region ?? ""}
          onChange={(event) => updateDraft("region", event.target.value)}
          disabled={busy}
        />
      </label>
      <label className="registration-form__field">
        <span>Объект поставки</span>
        <textarea
          value={draft.deliveryObject ?? ""}
          onChange={(event) =>
            updateDraft("deliveryObject", event.target.value)
          }
          rows={3}
          disabled={busy}
        />
      </label>
      <SmartCaptchaField onToken={setCaptchaToken} disabled={busy} />
      <label className="registration-form__consent">
        <input
          type="checkbox"
          checked={Boolean(draft.consentAccepted)}
          onChange={(event) =>
            updateDraft("consentAccepted", event.target.checked)
          }
          required
          disabled={busy}
        />
        <span>Согласен на обработку персональных данных</span>
      </label>

      <button type="submit" className="wizard-step__primary" disabled={busy || !draft.consentAccepted}>
        Зарегистрироваться
      </button>
    </form>
  );
}

function normalizeOptional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function describeError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Не удалось завершить регистрацию";
}
